/**
 * Bridge from a tile BODY to the Configure flow that `DashboardTab` owns
 * (its AddTileDialog edit mode). The error-state "Configure" button lives in
 * `QueryTileStates`, a different subtree from the dialog state — the tab
 * registers a per-dashboard handler here and the button renders ONLY while a
 * handler is registered (no dead buttons on surfaces that never wire one).
 * Mirrors the `setTileDangerousConfirmer` / chartSvgRegistry bridge pattern,
 * but as a zustand store so button visibility is reactive.
 */
import { create } from 'zustand';
import type { DashboardTile } from '../types/dashboardTypes';

export type TileConfigureHandler = (tile: DashboardTile) => void;

type TileConfigureBridgeState = {
  handlers: Record<string, TileConfigureHandler>;
  /** Returns an unregister fn; unregistering only removes OUR handler (a
   * remount that registered a fresh handler first must not be clobbered). */
  register: (dashboardId: string, handler: TileConfigureHandler) => () => void;
};

export const useTileConfigureBridgeStore = create<TileConfigureBridgeState>((set) => ({
  handlers: {},
  register: (dashboardId, handler) => {
    set((s) => ({ handlers: { ...s.handlers, [dashboardId]: handler } }));
    return () =>
      set((s) => {
        if (s.handlers[dashboardId] !== handler) return s;
        const handlers = { ...s.handlers };
        delete handlers[dashboardId];
        return { handlers };
      });
  },
}));

export const selectTileConfigureHandler =
  (dashboardId: string) =>
  (s: TileConfigureBridgeState): TileConfigureHandler | undefined =>
    s.handlers[dashboardId];
